import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Check, Sparkles, Zap, Rocket, AlertTriangle, Tag, X } from "lucide-react";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";

const STATE_COLORS = {
  trialing: "pill-info",
  active: "pill-success",
  past_due: "pill-warning",
  grace_period: "pill-warning",
  cancelled: "pill-neutral",
  expired: "pill-danger",
  suspended: "pill-danger",
};

const PLAN_ICONS = { ignite_monthly: Zap, ascend_monthly: Rocket };

function UsageBar({ label, current, max, suffix = "" }) {
  const pct = Math.min(100, (current / max) * 100);
  return (
    <div>
      <div className="flex justify-between text-xs mb-1.5">
        <span className="text-meta">{label}</span>
        <span className="text-ink font-semibold">{current}{suffix} / {max}{suffix}</span>
      </div>
      <div className="h-2 rounded-full bg-zinc-100 overflow-hidden">
        <div className={`h-full rounded-full ${pct > 85 ? "bg-warning" : "ashra-gradient-bg"}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

export default function Billing() {
  const { user, refresh } = useAuth();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(null);
  const [provider, setProvider] = useState("");           // "" = auto-select
  const [providers, setProviders] = useState({});
  const [paySettings, setPaySettings] = useState(null);
  const [currency, setCurrency] = useState("USD");
  const [usage, setUsage] = useState(null);
  const [sub, setSub] = useState(null);
  const [promo, setPromo] = useState("");
  const [promoApplied, setPromoApplied] = useState(null);
  const [promoBusy, setPromoBusy] = useState(false);

  const reload = async () => {
    const [p, u, s, prv, ps] = await Promise.all([
      api.get("/plans"),
      api.get("/usage"),
      api.get("/subscription"),
      api.get("/payments/providers").catch(() => ({ data: {} })),
      api.get("/payments/settings").catch(() => ({ data: null })),
    ]);
    setPlans(p.data); setUsage(u.data); setSub(s.data);
    setProviders(prv.data || {});
    setPaySettings(ps.data || null);
    if (ps.data?.default_currency) setCurrency(ps.data.default_currency);
  };

  useEffect(() => { reload(); }, []);

  const validatePromo = async () => {
    if (!promo.trim()) return;
    setPromoBusy(true);
    try {
      const { data } = await api.post("/promo/validate", { code: promo });
      setPromoApplied(data);
      toast.success(`${data.percent_off}% off applied — ${data.description}`);
    } catch (e) {
      setPromoApplied(null);
      toast.error(e?.response?.data?.detail || "Invalid code");
    }
    setPromoBusy(false);
  };

  const removePromo = () => { setPromo(""); setPromoApplied(null); };

  const upgrade = async (planId) => {
    setLoading(planId);
    try {
      const { data } = await api.post("/checkout/session", {
        package_id: planId,
        origin_url: window.location.origin,
        provider: provider || null,
        currency,
        ...(promoApplied ? { promo_code: promoApplied.code } : {}),
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout");
      setLoading(null);
    }
  };

  const cancel = async () => {
    if (!window.confirm("Cancel your subscription?")) return;
    try {
      await api.post("/subscription/cancel");
      toast.success("Subscription cancelled");
      await refresh();
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA] p-6 lg:p-10" data-testid="billing-page">
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-8">
        <div>
          <h1 className="font-display text-3xl font-bold text-ink">Billing & Plans</h1>
          <p className="text-sm text-meta mt-1 inline-flex items-center gap-2">
            Current plan: <span className="text-ink capitalize font-semibold">{user?.plan}</span>
            {sub?.state && <span className={`pill ${STATE_COLORS[sub.state] || "pill-neutral"}`}>{sub.state.replace("_"," ")}</span>}
          </p>
        </div>
        {sub?.state === "active" && user?.plan !== "free" && (
          <button onClick={cancel} className="rounded-full border border-rose-200 bg-danger-soft text-danger hover:bg-rose-100 px-4 py-2 text-sm font-semibold transition focus-ring" data-testid="cancel-sub">
            Cancel subscription
          </button>
        )}
      </div>

      {usage && (
        <div className="mb-8 surface p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display text-lg font-bold text-ink inline-flex items-center gap-2"><Zap className="h-4 w-4 text-warning" /> AI usage</h3>
            <span className="text-xs text-meta font-semibold capitalize">Plan: {usage.plan}</span>
          </div>
          <div className="grid sm:grid-cols-2 gap-5">
            <UsageBar label="AI calls today" current={usage.daily.calls} max={usage.daily.limit_calls} />
            <UsageBar label="AI cost (this month)" current={parseFloat(usage.monthly.cost.toFixed(2))} max={usage.monthly.limit_cost_usd} suffix="$" />
          </div>
          {(usage.daily.calls / usage.daily.limit_calls > 0.8) && (
            <div className="mt-4 inline-flex items-start gap-2 text-warning text-sm rounded-xl border border-amber-200 bg-warning-soft px-3 py-2 font-medium">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" /> You're nearing your daily AI limit — upgrade to unlock more revenue intelligence.
            </div>
          )}
        </div>
      )}

      {/* Promo code */}
      <div className="mb-6 surface p-5">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
          <h3 className="font-display text-base font-bold text-ink inline-flex items-center gap-2"><Tag className="h-4 w-4 text-ai" /> Promo code</h3>
          {promoApplied && (
            <span className="pill pill-success" data-testid="promo-applied-pill">
              {promoApplied.percent_off}% off · {promoApplied.code}
              <button onClick={removePromo} className="ml-1" data-testid="promo-remove-btn"><X className="h-3 w-3" /></button>
            </span>
          )}
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          <input
            value={promo}
            onChange={(e) => setPromo(e.target.value.toUpperCase())}
            placeholder="Enter promo code (e.g. ASHRA20)"
            className="flex-1 rounded-xl bg-white border border-line px-3 py-2 text-sm text-ink placeholder:text-meta-soft focus:outline-none focus:ring-2 focus:ring-violet-500 font-mono"
            data-testid="promo-input"
          />
          <button onClick={validatePromo} disabled={promoBusy || !promo.trim()} className="rounded-full ashra-gradient-bg px-5 py-2 text-sm font-semibold text-white inline-flex items-center justify-center gap-1 shadow-sm hover:shadow-md disabled:opacity-50 transition focus-ring" data-testid="promo-apply-btn">
            <Tag className="h-3.5 w-3.5" /> {promoBusy ? "Validating…" : "Apply"}
          </button>
        </div>
        <p className="text-xs text-meta-soft mt-2">Discount applies on the next checkout.</p>
      </div>

      <div className="mb-6 flex flex-wrap items-center gap-3" data-testid="provider-switch-row">
        <div className="inline-flex rounded-full bg-white border border-line p-1 shadow-sm" data-testid="provider-switch">
          {[
            { id: "", label: "Auto" },
            { id: "paddle", label: "Paddle" },
            { id: "stripe", label: "Stripe" },
            { id: "razorpay", label: "Razorpay" },
          ].map((p) => {
            const status = providers[p.id];
            const sandbox = p.id && status?.sandbox;
            return (
              <button key={p.id || "auto"} onClick={() => setProvider(p.id)}
                className={`px-4 py-2 text-xs font-semibold rounded-full transition focus-ring ${provider === p.id ? "ashra-gradient-bg text-white shadow-sm" : "text-meta hover:text-ink"}`}
                data-testid={`provider-${p.id || "auto"}`}>
                {p.label}{sandbox && <span className="ml-1 text-[10px] opacity-70">sandbox</span>}
              </button>
            );
          })}
        </div>
        {paySettings && (
          <div className="text-xs text-meta">
            Currency:{" "}
            <select
              value={currency}
              onChange={(e) => setCurrency(e.target.value)}
              className="rounded-full border border-line bg-white px-3 py-1.5 text-xs font-semibold text-ink focus:outline-none focus:ring-1 focus:ring-violet-500"
              data-testid="billing-currency-select"
            >
              {(paySettings.supported_currencies || ["USD"]).map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      <div className="grid lg:grid-cols-2 gap-4 max-w-4xl">
        {plans.map((p) => {
          const Icon = PLAN_ICONS[p.id] || Sparkles;
          const current = (user?.plan && p.id.startsWith(user.plan));
          const featured = !!p.highlight || p.id === "ascend_monthly";
          const finalMonthly = promoApplied ? Math.round(p.price_monthly * (1 - promoApplied.percent_off / 100)) : p.price_monthly;
          return (
            <div key={p.id} className={`rounded-3xl p-6 relative overflow-hidden transition ${featured ? "bg-gradient-to-br from-violet-500 via-fuchsia-500 to-pink-500 p-[1px]" : "surface"}`} data-testid={`plan-${p.id}`}>
              <div className={featured ? "rounded-3xl bg-white p-6" : ""}>
                <div className="flex items-center justify-between mb-3">
                  <div className={`h-10 w-10 rounded-2xl ${featured ? "ashra-gradient-bg" : "bg-zinc-100"} grid place-items-center`}>
                    <Icon className={`h-5 w-5 ${featured ? "text-white" : "text-ai"}`} />
                  </div>
                  {featured && <span className="pill pill-ai">Recommended</span>}
                </div>
                <h3 className="font-display text-2xl font-extrabold text-ink">{p.name}</h3>
                <p className="text-sm text-meta mt-1">{p.tagline}</p>
                <div className="mt-4 flex items-baseline gap-2">
                  {promoApplied && (
                    <span className="font-display text-xl text-meta-soft line-through">${p.price_monthly}</span>
                  )}
                  <p className="font-display text-4xl font-extrabold text-ink">${finalMonthly}<span className="text-sm text-meta font-medium">/mo</span></p>
                </div>
                <p className="text-xs text-meta">${promoApplied ? Math.round(p.price_yearly * (1 - promoApplied.percent_off / 100)) : p.price_yearly}/yr · save ~17%</p>
                <ul className="mt-5 space-y-2 text-sm">
                  {p.features.map(f => (
                    <li key={f} className="flex gap-2"><Check className="h-4 w-4 text-success mt-0.5 shrink-0" /><span className="text-ink-soft">{f}</span></li>
                  ))}
                </ul>
                <button
                  disabled={loading === p.id || current}
                  onClick={() => upgrade(p.id)}
                  className={`mt-6 w-full rounded-full py-3 text-sm font-bold inline-flex items-center justify-center gap-2 transition focus-ring ${current ? "bg-zinc-100 text-meta cursor-not-allowed" : "ashra-gradient-bg text-white hover:shadow-lg"}`}
                  data-testid={`upgrade-${p.id}`}
                >
                  <Sparkles className="h-4 w-4" />
                  {current ? "Current plan" : loading === p.id ? "Opening checkout…" : (provider ? `Upgrade via ${provider}` : "Upgrade")}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
